import { memo, useMemo } from 'react'
import { useField, UseFieldConfig } from 'react-final-form'
import { Input, InputProps, useIMask } from '@cargomart/ui'
import { checkFieldError } from 'helpers/form'
import { MaskedPatternOptions } from 'imask'
import { isRequiredError } from 'src/helpers/error'
import { IconTooltipError } from 'src/shared/components/IconTooltip'

interface IProps extends Omit<InputProps, 'onChange' | 'onFocus' | 'onBlur' | 'type' | 'error' | 'value'> {
  name: string
  definitions?: MaskedPatternOptions['definitions']
  isShowTooltip?: boolean
  mask?: any
  onComplete?: (value: string, masked: any) => void
  testId?: string
  validate?: UseFieldConfig<string>['validate']
}

export const FieldIMaskNoMemo = ({
  name,
  validate,
  mask,
  onComplete,
  size = 'nm',
  definitions,
  testId,
  isShowTooltip = false,
  ...props
}: IProps) => {
  const { input, meta } = useField<string>(name, {
    validate,
    parse: (value) => value || ''
  })
  const { value, onChange, onBlur, onFocus } = input
  const error = checkFieldError(meta)
  const hasError = error && !isRequiredError(error)
  const { ref } = useIMask({ mask, definitions }, { onAccept: onChange, onComplete }, { value })

  const renderPostfix = useMemo(() => {
    if (isShowTooltip && hasError) {
      return (
        <div className="ml-1 flex items-center">
          <IconTooltipError className="!text-negative" containerSize="nm" placement="top-end">
            {error}
          </IconTooltipError>
        </div>
      )
    }

    return undefined
  }, [hasError, isShowTooltip, error])

  return (
    <Input
      {...props}
      data-test-id={testId}
      inputRef={ref}
      invalid={!!checkFieldError(meta)}
      name={name}
      onBlur={onBlur}
      onFocus={onFocus}
      postfix={renderPostfix}
      size={size}
      type="text"
    />
  )
}

export const FieldIMask = memo(FieldIMaskNoMemo) as typeof FieldIMaskNoMemo
